import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Coins, Check, Loader2 } from 'lucide-react';
import MyCleanStudioHeader from './MyCleanStudioHeader';
import MyCleanStudioFooter from './MyCleanStudioFooter';
import { selectIsAuthenticated } from '../features/auth/authSlice';
import { createCheckout, selectCreditBalance } from '../features/billing/billingSlice';
import {
  CREDIT_PACKS,
  SCAN_PRICING_ROWS,
  SIGNUP_BONUS_CREDITS,
  formatCents,
  perCreditPrice,
} from '../features/billing/constants';
import './PricingPage.css';
import { track } from '../analytics/analytics';

function PricingPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const balance = useSelector(selectCreditBalance);

  useEffect(() => {
    track('pricing_viewed', { account_type: isAuthenticated ? 'signed_up' : 'anonymous' });
    // Once per visit, not on every sign-in state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track which pack's checkout is in flight so only its button spins.
  const [pendingPackId, setPendingPackId] = useState(null);
  const [error, setError] = useState(null);

  const handleBuy = async (packId) => {
    setError(null);
    if (!isAuthenticated) {
      // Purchases require a signed-up account. Send them to sign up / log in.
      navigate('/signup');
      return;
    }
    setPendingPackId(packId);
    try {
      const data = await dispatch(createCheckout({ packId })).unwrap();
      // Redirect to Stripe Checkout. Credits land via the webhook on return.
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Could not start checkout. Please try again.');
      setPendingPackId(null);
    }
  };

  return (
    <div className="pricing-page">
      <MyCleanStudioHeader />

      <main className="pricing-content">
        <header className="pricing-header">
          <h1 className="pricing-title">Add credits</h1>
          <p className="pricing-subtitle">
            Pay only for what you scan. Credits never expire, and there's no
            subscription. Need more later? Just buy again — as many times as
            you like.
          </p>
          {isAuthenticated && balance != null && (
            <div className="pricing-balance">
              <Coins size={16} className="pricing-balance-icon" aria-hidden="true" />
              You have <strong>{balance.toLocaleString()}</strong> credits
            </div>
          )}
        </header>

        {error && <div className="pricing-error" role="alert">{error}</div>}

        <section className="pricing-packs" aria-label="Credit packs">
          {CREDIT_PACKS.map((pack) => {
            const isPending = pendingPackId === pack.id;
            const disabled = pendingPackId != null;
            return (
              <div
                key={pack.id}
                className={`pricing-pack-card${pack.popular ? ' popular' : ''}`}
              >
                {pack.popular && <span className="pricing-pack-badge">Most popular</span>}
                <h2 className="pricing-pack-name">{pack.name}</h2>
                <div className="pricing-pack-credits">
                  <Coins size={18} className="pricing-pack-credits-icon" aria-hidden="true" />
                  {pack.credits.toLocaleString()} credits
                </div>
                <div className="pricing-pack-price">{formatCents(pack.priceCents)}</div>
                <div className="pricing-pack-percredit">{perCreditPrice(pack)}</div>
                <p className="pricing-pack-blurb">{pack.blurb}</p>
                <button
                  className="pricing-pack-buy"
                  onClick={() => handleBuy(pack.id)}
                  disabled={disabled}
                >
                  {isPending ? (
                    <>
                      <Loader2 size={16} className="pricing-spin" aria-hidden="true" />
                      Starting…
                    </>
                  ) : (
                    'Buy'
                  )}
                </button>
              </div>
            );
          })}
        </section>

        <section className="pricing-scan-table" aria-label="How scans are priced">
          <h2 className="pricing-section-title">How scans are priced</h2>
          <p className="pricing-section-note">
            A scan costs credits based on the video's length. Transcript-only
            scans cost 10 credits.
          </p>
          <table className="pricing-table">
            <thead>
              <tr>
                <th>Video length</th>
                <th>Credits</th>
              </tr>
            </thead>
            <tbody>
              {SCAN_PRICING_ROWS.map((row) => (
                <tr key={row.length}>
                  <td>{row.length}</td>
                  <td>{row.credits}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="pricing-signup-note">
            <Check size={16} className="pricing-check" aria-hidden="true" />
            New accounts start with <strong>{SIGNUP_BONUS_CREDITS} free credits</strong>.
          </p>
        </section>
      </main>

      <MyCleanStudioFooter />
    </div>
  );
}

export default PricingPage;
